import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Row, Col, Label, Input, FormFeedback, Spinner,
} from "reactstrap";
import { SearchSelectField } from "../ui/SearchSelect";
import { DateRangeField } from "../ui/DateRange";
import { FileUploadField } from "../ui/FileUpload";

/**
 * 共用 CRUD 表單彈窗（主計劃：新增/異動共版）。
 *
 * Formik + Yup 驅動。fields 設定即生成表單；未提供 validationSchema 時，
 * 依 required 自動組基本必填驗證。送出交給 onSubmit（可串審核送出）。
 *
 *   <CrudFormModal
 *     isOpen toggle title="新增使用者"
 *     fields={[
 *       { name:"name",    label:"姓名", type:"text",   required:true, span:6 },
 *       { name:"roleId",  label:"角色", type:"select", required:true, span:6,
 *         options:[{value:1,label:"管理員"}] },
 *       { name:"note",    label:"備註", type:"textarea", span:12 },
 *     ]}
 *     initialValues={editing || {}}
 *     onSubmit={async (values)=> { await usersApi.create(values); }}
 *   />
 */
function autoSchema(fields) {
  const shape = {};
  fields.forEach((f) => {
    if (!f.required) return;
    let rule = Yup.string();
    if (f.type === "number") rule = Yup.number().typeError("請輸入數字");
    if (f.type === "email") rule = Yup.string().email("Email 格式不正確");
    shape[f.name] = rule.required(`請輸入${f.label}`);
  });

  return Yup.object(shape);
}

const CrudFormModal = ({
  isOpen, toggle, title, fields,
  initialValues = {}, validationSchema, onSubmit,
  submitLabel = "儲存",
}) => {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: fields.reduce(
      (acc, f) => ({ ...acc, [f.name]: initialValues[f.name] ?? "" }),
      {}
    ),
    validationSchema: validationSchema || autoSchema(fields),
    onSubmit: async (values, helpers) => {
      await onSubmit(values);
      helpers.setSubmitting(false);
      toggle();
    },
  });

  // 每次打開都要是乾淨的表單。只靠 enableReinitialize 不夠 ——
  // 連續兩次「新增」的 initialValues 都是 {},formik 認為沒變就不重置,
  // 上一次打到一半的字會留在那裡。
  useEffect(() => {
    if (isOpen) formik.resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const invalid = (name) => formik.touched[name] && !!formik.errors[name];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row className="g-3">
            {fields.map((f) => (
              <Col md={f.span || 6} key={f.name}>
                <Label className="form-label">
                  {f.label}{f.required ? <span className="text-danger"> ＊</span> : null}
                </Label>
                {f.type === "search-select" ? (
                  // 長清單欄位(機房/廠商/關聯設備…)可打字過濾
                  <SearchSelectField
                    name={f.name}
                    value={formik.values[f.name]}
                    onChange={(v) => formik.setFieldValue(f.name, v)}
                    onBlur={() => formik.setFieldTouched(f.name, true)}
                    options={f.options || []}
                    isMulti={f.isMulti}
                    placeholder={f.placeholder || "請選擇"}
                    error={invalid(f.name) ? formik.errors[f.name] : ""}
                  />
                ) : f.type === "date-range" ? (
                  <DateRangeField
                    name={f.name}
                    value={formik.values[f.name] || []}
                    onChange={(v) => formik.setFieldValue(f.name, v)}
                    placeholder={f.placeholder}
                    error={invalid(f.name) ? formik.errors[f.name] : ""}
                  />
                ) : f.type === "file" ? (
                  <FileUploadField
                    name={f.name}
                    files={formik.values[f.name] || []}
                    onChange={(v) => formik.setFieldValue(f.name, v)}
                    multiple={f.multiple !== false}
                    accept={f.accept}
                    maxSize={f.maxSize}
                    error={invalid(f.name) ? formik.errors[f.name] : ""}
                  />
                ) : f.type === "select" ? (
                  <Input
                    type="select"
                    name={f.name}
                    value={formik.values[f.name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={invalid(f.name)}
                  >
                    <option value="">請選擇</option>
                    {(f.options || []).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Input>
                ) : (
                  <Input
                    type={f.type || "text"}
                    name={f.name}
                    rows={f.type === "textarea" ? 3 : undefined}
                    placeholder={f.placeholder || ""}
                    value={formik.values[f.name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={invalid(f.name)}
                  />
                )}
                <FormFeedback>{formik.errors[f.name]}</FormFeedback>
                {f.hint ? <div className="text-muted small mt-1">{f.hint}</div> : null}
              </Col>
            ))}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={toggle}>取消</Button>
          <Button color="primary" type="submit" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? <Spinner size="sm" className="me-1" /> : null}{submitLabel}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

CrudFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  title: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      required: PropTypes.bool,
      options: PropTypes.array,
      span: PropTypes.number,
      placeholder: PropTypes.string,
    })
  ).isRequired,
  initialValues: PropTypes.object,
  validationSchema: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
};

export default CrudFormModal;
